// /*
//   This is used to manage stripe payments
//
//   NOTE – API utilizes a copy of this file. If changes are made here, they must also be updated
//          within API
//
// */

const isProduction = process.env.VITE_IS_PROD === 'true'

/*
  Pricing Updates
  — Updated on Oct 3, 2022
  - April 21, 2023
    Clarification on naming - A "Product" on Stripe is referred to as a "Plan" in code
    Structure - implemented StripePlans, StripePlan, StripePlanDetails, StripePlanList
  — May 4, 2023
    Sets the correct monthly & annual production pricing for Basic Solo and Basic Group
  - Oct 4, 2023
    Adds products for the new subscription tiers
*/

export enum StripePlans {
  basic = 'basic',
  basic_solo = 'basic_solo',
  basic_group = 'basic_group',
  sole_prop_solo = 'sole_prop_solo',
  scorp_solo = 'scorp_solo',
  basic_no_qte = 'basic_no_qte',
}

export interface StripePlanDetails {
  // When monthly and annual prices are separate products
  productId?: string

  priceId: string
  amount: string
  amountInCents: number
}

export interface StripePlan {
  // When monthly and annual prices are contained in the same product (Basic plan only)
  productId?: string
  productName?: string
  annual: StripePlanDetails
  monthly: StripePlanDetails
}

export type StripePlanList = {
  [key in StripePlans]: StripePlan
}

export const HEARD_LITE_PRODUCT_IDS: string[] = [
  // STAGING
  'prod_UPm8xVL1ZRgEFj', // annual
  'prod_UPm5Ny1i0gwx08', // monthly
  // PROD
  'prod_URGEPUboxfnuG4', // annual
  'prod_URGDMAuhLI6TFd', // monthly
]

export const STARTER_PLAN_PRODUCT_IDS = [
  // STAGING
  'prod_SLzeW9kZz7GBSY',
  'prod_SLzdNJI7KuBGri',
  'prod_SLzdCV74ZJWF8A',
  'prod_SLzcGs3OGEv6Mz',
  'prod_SLzcZLghlmubXE',
  'prod_SLzbjjIismNKHf',
  'prod_SLzaV5lbQLIDOW',
  'prod_SLzZUrovzotEzY',
  // PROD
  'prod_SPNZ4auU04Hybp',
  'prod_SPNZKZM5uNGMbS',
  'prod_SPNZwjOuNO5pQJ',
  'prod_SPNZnM71UfFGVz',
  'prod_SPNZora5lulP7u',
  'prod_SPNZKjwJ08ZMcA',
  'prod_SPNYFe3vpGaTT3',
  'prod_SPNYz1T5r29FzL',
]

const TEST_STRIPE_PLANS: StripePlanList = {
  sole_prop_solo: {
    productName: 'Solo Practice - Sole Proprietor',
    monthly: {
      productId: 'prod_Ol4nyBTNpDGfPQ',
      priceId: 'price_1NxYeIJy69P78a4E3KMMDyLY',
      amount: '100',
      amountInCents: 10000,
    },
    annual: {
      productId: 'prod_Ol4s4rwpZepQe4',
      priceId: 'price_1NxYiYJy69P78a4EMsxI4LVF',
      amount: '900',
      amountInCents: 90000,
    },
  },
  scorp_solo: {
    productName: 'Solo Practice - S Corporation',
    monthly: {
      productId: 'prod_Ol4unCLsUQkbl3',
      priceId: 'price_1NxYkPJy69P78a4ED61gvQf1',
      amount: '200',
      amountInCents: 20000,
    },
    annual: {
      productId: 'prod_Ol4vGR4g4FjJSf',
      priceId: 'price_1NxYlFJy69P78a4EbX7BO7Gd',
      amount: '1800',
      amountInCents: 180000,
    },
  },
  basic: {
    productId: 'prod_Sbik6VooWjIFhQ',
    annual: {
      priceId: 'price_1RgVNQJy69P78a4EVgAP25ZF',
      amount: '1548',
      amountInCents: 154800,
    },
    monthly: {
      priceId: 'price_1RgVJJJy69P78a4E95aN9UzX',
      amount: '149',
      amountInCents: 14900,
    },
  },
  basic_solo: {
    productId: 'prod_Nlz5AMsS0MzNYt',
    annual: {
      priceId: 'price_1R58mPJy69P78a4EXJfkY6Xd',
      amount: '1548',
      amountInCents: 154800,
    },
    monthly: {
      priceId: 'price_1R58m4Jy69P78a4Ejjh7RUzX',
      amount: '149',
      amountInCents: 14900,
    },
  },
  basic_group: {
    productId: 'prod_Nlz7Wdg9VNQvno',
    annual: {
      priceId: 'price_1R58j1Jy69P78a4EGJBfgB9f',
      amount: '1548',
      amountInCents: 154800,
    },
    monthly: {
      priceId: 'price_1R58iNJy69P78a4EwRYzozA0',
      amount: '149',
      amountInCents: 14900,
    },
  },
  basic_no_qte: {
    productId: 'prod_Ry6BPAoMOfOOWi',
    annual: {
      priceId: 'price_1R5EcPJy69P78a4EYu7fPVry',
      amount: '1020',
      amountInCents: 102000,
    },
    monthly: {
      priceId: 'price_1R5EcAJy69P78a4EJqksBcuV',
      amount: '99',
      amountInCents: 9900,
    },
  },
}

const PROD_STRIPE_PLANS: StripePlanList = {
  sole_prop_solo: {
    productName: 'Solo Practice - Sole Proprietor',
    monthly: {
      productId: 'prod_SZWOoqNcvpkSVr',
      priceId: 'price_1ReNLfJy69P78a4EYZeuaqNb',
      amount: '199',
      amountInCents: 19900,
    },
    annual: {
      productId: 'prod_SZWNPrrXLzNS2n',
      priceId: 'price_1ReNL5Jy69P78a4EZSjtImvv',
      amount: '2028',
      amountInCents: 202800,
    },
  },
  scorp_solo: {
    productName: 'Solo Practice - S Corporation',
    monthly: {
      productId: 'prod_SZWim4tlukOT7d',
      priceId: 'price_1ReNfUJy69P78a4Ecn0geLz6',
      amount: '299',
      amountInCents: 29900,
    },
    annual: {
      productId: 'prod_SZWimGAycu73u6',
      priceId: 'price_1ReNf7Jy69P78a4EVvb7fBI2',
      amount: '3060',
      amountInCents: 306000,
    },
  },
  basic: {
    productId: 'prod_SbhZwyTiMR8J47',
    annual: {
      priceId: 'price_1RgUAoJy69P78a4EVnnyedQz',
      amount: '1548',
      amountInCents: 154800,
    },
    monthly: {
      priceId: 'price_1RgVPKJy69P78a4EgI18DuBr',
      amount: '149',
      amountInCents: 14900,
    },
  },
  basic_solo: {
    productId: 'prod_Npbu9p0ENImpqa',
    annual: {
      priceId: 'price_1R58pqJy69P78a4EjIBjvuY0',
      amount: '1548',
      amountInCents: 154800,
    },
    monthly: {
      priceId: 'price_1R58paJy69P78a4EiQlJABRb',
      amount: '149',
      amountInCents: 14900,
    },
  },
  basic_group: {
    productId: 'prod_NpbwnfSa8HiQ6z',
    annual: {
      priceId: 'price_1R58nsJy69P78a4ES36y4DCZ',
      amount: '1548',
      amountInCents: 154800,
    },
    monthly: {
      priceId: 'price_1R58nXJy69P78a4E9J9zH64E',
      amount: '149',
      amountInCents: 14900,
    },
  },
  basic_no_qte: {
    productId: 'prod_Ry6PfWX6z3AbA0',
    annual: {
      priceId: 'price_1R4ACOJy69P78a4EfskVbeNM',
      amount: '1020',
      amountInCents: 102000,
    },
    monthly: {
      priceId: 'price_1R4ACOJy69P78a4EW3KPxI4M',
      amount: '99',
      amountInCents: 9900,
    },
  },
}
export const fetchStripePlans = () => {
  if (isProduction) {
    return PROD_STRIPE_PLANS
  } else {
    return TEST_STRIPE_PLANS
  }
}

export const CATCHUP_BOOKKEEPING_PRICE_PER_MONTH = 99
export const testCatchupBkPriceId = 'price_1PO7PGJy69P78a4EjM4pDmaA'
export const prodCatchupBkPriceId = 'price_1PO3m0Jy69P78a4EYiXdSuLO'
export const testTrialCatchupBkPriceId = 'price_1PyL1mJy69P78a4E4wPU6jkp'
export const prodTrialCatchupBkPriceId = 'price_1Q0RIyJy69P78a4EVzukHYgT'

// These are Financial Advisory Products
const TEST_ADVISORY_PRICING = {
  productName: 'Financial Advisory',
  monthly: {
    productId: 'prod_RFDUlSbJq8PMjb',
    priceId: 'price_1QMj3JJy69P78a4EqLoTF1d4',
    amount: '29',
  },
  annual: {
    productId: 'prod_RFDWEcFZX1mcRl',
    priceId: 'price_1QMj54Jy69P78a4ES3kyTVHA',
    amount: '300',
  },
}

export const PROD_ADVISORY_PRICING = {
  productName: 'Financial Advisory',
  monthly: {
    productId: 'prod_RFDYx4Gh1Dsjpl',
    priceId: 'price_1QMj7WJy69P78a4EnpzpB28B',
    amount: '29',
  },
  annual: {
    productId: 'prod_RFDYCjurz5LwwH',
    priceId: 'price_1QMj7SJy69P78a4ECg5RDPEy',
    amount: '300',
  },
}

export const fetchAdvisoryPrices = () => {
  if (isProduction) {
    return PROD_ADVISORY_PRICING
  } else {
    return TEST_ADVISORY_PRICING
  }
}

export enum AnnualCommitmentDiscount {
  FIFTEEN_PERCENT = 15,
  TEN_PERCENT = 10,
}

const productIdToAnnualCommitmentPriceMap = {
  // Staging

  // Group s-corp (monthly) -- remove post 2025 Tax launch
  prod_Roek1FR4loyYc7: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RLvtUJy69P78a4EsRfGvbAq',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPM1Jy69P78a4ElSfN5RWV',
      amountInCents: 26900,
    },
  },
  // Group s-corp (monthly)
  prod_TA8iH84UnXl8gw: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGIlvJy69P78a4EFlYgmccI',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGInZJy69P78a4E3NMOJ25e',
      amountInCents: 26900,
    },
  },
  // Group sole prop (monthly) -- remove post 2025 Tax launch
  prod_Rpsj0tVFaSBVUi: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RLw12Jy69P78a4EtSVXUdXe',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPNXJy69P78a4EKBeanDsW',
      amountInCents: 26900,
    },
  },
  // Group sole prop (monthly)
  prod_TACUvFdryZ6AIy: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGIpRJy69P78a4EnLejZMiP',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGIrQJy69P78a4EYWM6r6t5',
      amountInCents: 26900,
    },
  },
  // Solo s-corp (monthly) -- remove post 2025 Tax launch
  prod_Ol4unCLsUQkbl3: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RMBCoJy69P78a4EH7yOJZKU',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPOEJy69P78a4ENBmMcz8W',
      amountInCents: 26900,
    },
  },
  // Solo s-corp (monthly)
  prod_TADTZnEC1JXOyT: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGItOJy69P78a4EluMgJPvS',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGIvWJy69P78a4EqzHlyCCC',
      amountInCents: 26900,
    },
  },
  // Solo sole prop (monthly) -- remove post 2025 Tax launch
  prod_Ol4nyBTNpDGfPQ: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RMBH2Jy69P78a4EMctTForI',
      amountInCents: 16900,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPOzJy69P78a4EmWgj1FfM',
      amountInCents: 17900,
    },
  },
  // Solo sole prop (monthly)
  prod_TACIvBPv7u90l8: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGIxeJy69P78a4Ei9DjsPso',
      amountInCents: 16900,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGJ1NJy69P78a4E7v4EW0zv',
      amountInCents: 17900,
    },
  },
  // Solo sole prop starter (monthly)
  prod_SLzZUrovzotEzY: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZIWyJy69P78a4E83jgwwiL',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPPQJy69P78a4EWUFelNq2',
      amountInCents: 5300,
    },
  },
  // Group sole prop starter (monthly)
  prod_SLzbjjIismNKHf: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZJbAJy69P78a4EPX31Sxqf',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPQ0Jy69P78a4EvCKvREJr',
      amountInCents: 5300,
    },
  },
  // Solo s-corp starter (monthly)
  prod_SLzcGs3OGEv6Mz: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZJbMJy69P78a4ESm76pvVq',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPQRJy69P78a4EtSxF91TS',
      amountInCents: 5300,
    },
  },
  // Group s-corp starter (monthly)
  prod_SLzdNJI7KuBGri: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZJbWJy69P78a4ERGZff1tg',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPQrJy69P78a4EcjeNUMCf',
      amountInCents: 5300,
    },
  },

  // Production

  // Group s-corp (monthly)
  prod_RpsmFSxHJfKvzl: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RMCEXJy69P78a4EJOC3DQbd',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPSOJy69P78a4Ed2SVZkw8',
      amountInCents: 26900,
    },
  },
  // Group sole prop (monthly)
  prod_RpslSNupUPTovA: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RMCDNJy69P78a4EOTin3U8H',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPSqJy69P78a4E9k6IXzSm',
      amountInCents: 26900,
    },
  },
  // Solo s-corp (monthly)
  prod_Onizu0f68MwUej: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RMCAAJy69P78a4EOF23QbmS',
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPTTJy69P78a4EskWIfRCI',
      amountInCents: 26900,
    },
  },
  // Solo sole prop (monthly)
  prod_OnixhO5vKVHn8j: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RMCBmJy69P78a4EGkA4N4hv',
      amountInCents: 16900,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPTwJy69P78a4EWhsuQvZU',
      amountInCents: 17900,
    },
  },
  // Solo sole prop starter (monthly)
  prod_SPNZ4auU04Hybp: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZJhMJy69P78a4EIxLB4Qhi',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPUWJy69P78a4EEg8yPZkh',
      amountInCents: 5300,
    },
  },
  // Group sole prop starter (monthly)
  prod_SPNZwjOuNO5pQJ: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZJhWJy69P78a4E5ZcVcb3k',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPUtJy69P78a4E3kDEHT4o',
      amountInCents: 5300,
    },
  },
  // Solo s-corp starter (monthly)
  prod_SPNZora5lulP7u: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZJhfJy69P78a4E51rZHJW8',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPVVJy69P78a4EkIYvMcPS',
      amountInCents: 5300,
    },
  },
  // Group s-corp starter (monthly)
  prod_SPNYFe3vpGaTT3: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RZJhoJy69P78a4En3V2irYR',
      amountInCents: 5000,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrPWQJy69P78a4Evk1vqCDr',
      amountInCents: 5300,
    },
  },
  // New plans as of July 2025
  prod_SZWYfYrRofwmot: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RgTcdJy69P78a4E3PjMhrOs', // Group s-corp (monthly)
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrQCQJy69P78a4EhldS3voE',
      amountInCents: 26900,
    },
  },
  prod_SZVsUofqDTmGUj: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1ReOKbJy69P78a4E1mMJD1DL', // Group sole prop (monthly)
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrQDDJy69P78a4E1Vq134Lz',
      amountInCents: 26900,
    },
  },
  prod_SZWim4tlukOT7d: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RgTeYJy69P78a4EMAmc5F6n', // Solo s-corp (monthly)
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrQDsJy69P78a4E6ZCmFlpd',
      amountInCents: 26900,
    },
  },
  prod_SZWOoqNcvpkSVr: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1RgTfNJy69P78a4ENOUOyDb7', // Solo sole prop (monthly)
      amountInCents: 16900,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1RrQF3Jy69P78a4EnZCO3TH4',
      amountInCents: 17900,
    },
  },
  // New plans as of October 2025
  prod_TCjU1tFHq94eG1: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGK6fJy69P78a4EnSAW208u', // Group s-corp (monthly)
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGK7DJy69P78a4Eor71laiw',
      amountInCents: 26900,
    },
  },
  prod_TCk1KdhFqXEr9F: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGKynJy69P78a4EcYV3EaDB', // Group sole prop (monthly)
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGKzZJy69P78a4EinAhbt2c',
      amountInCents: 26900,
    },
  },
  prod_TCkzCfzfZ50lrF: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGLTrJy69P78a4E3rkPyhb0', // Solo s-corp (monthly)
      amountInCents: 25500,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGLV8Jy69P78a4ELHTHy4Hv',
      amountInCents: 26900,
    },
  },
  prod_TCjrHSM3IhCFfz: {
    [AnnualCommitmentDiscount.FIFTEEN_PERCENT]: {
      id: 'price_1SGKRMJy69P78a4ENPRRhn3t', // Solo sole prop (monthly)
      amountInCents: 16900,
    },
    [AnnualCommitmentDiscount.TEN_PERCENT]: {
      id: 'price_1SGKRxJy69P78a4EM6OAFfYI',
      amountInCents: 17900,
    },
  },
}

export const getAnnualCommitmentPriceIdFromProductId = (
  productId: string,
  discount: AnnualCommitmentDiscount = AnnualCommitmentDiscount.FIFTEEN_PERCENT
) => {
  const keyInMap = productId as keyof typeof productIdToAnnualCommitmentPriceMap
  return productIdToAnnualCommitmentPriceMap[keyInMap]?.[discount] ?? null
}
